/**
 * bimLuminaireSpec — the live luminaire layer's pure half (card t_2dc17719).
 *
 * The owner's ask: *"we would like the light elements to be glowing when
 * on…"*. A luminaire's live state is not geometry — unlike a shade, a light
 * does not move when it is switched — so the layer does not render a second
 * copy of the fixture: it drives the ONE part of the served model's own baked
 * twin that is the light itself, the opal diffuser, and adds the halo.
 *
 *   widget config `id`        -> which hub device the icon is bound to
 *   entities store device row -> power / brightness / colour (socket-driven)
 *   the served model's twin    -> `Luminaire - <device name>`, the parametric
 *                                 fixture A2 authored (IfcLightFixture)
 *   its transparent part       -> the emitter/lens (the only part styled
 *                                 `Torik luminaire - diffuser (opal)`,
 *                                 transparency 0.22 — see luminaire.py)
 *
 * Everything here is pure — no Vue, no three.js — so the matching rules and the
 * state maths are unit-testable; the geometry numbers below are the house-ifc
 * module's own constants (devices/luminaire/luminaire.py + schema.json), pinned
 * by `bimLuminaireSpec.test.ts` so a look-alike drift fails a test rather than
 * the picture.
 *
 * The one rule that must survive every change here: **a light whose device
 * reports no state is left exactly as the hub served it.** Nothing is hidden,
 * nothing is dimmed, no halo is drawn — the baked twin is the fallback view.
 */

import type { Device } from '@/types';

export type Vec3 = [number, number, number];

// ─── the family's own numbers (devices/luminaire/luminaire.py) ──────────────

export const LUMINAIRE_VARIANTS = ['spot', 'pendant', 'wall', 'pole', 'strip', 'bulb', 'panel'] as const;
export type LuminaireVariant = (typeof LUMINAIRE_VARIANTS)[number];

/** Per-variant default sizes in mm (luminaire.py `DEFAULTS`). */
export const LUMINAIRE_DEFAULT_SIZE_MM: Record<LuminaireVariant, Record<string, number>> = {
  spot: { dia: 88, height: 94 },
  pendant: { shade_dia: 380, shade_h: 260, drop: 800 },
  wall: { w: 110, h: 320, depth: 86 },
  pole: { h: 2100, head_dia: 120 },
  strip: { length: 1200 },
  bulb: { bulb_dia: 94, drop: 212 },
  panel: { length: 900, width: 70 },
};

/** The module's tones, the same numbers the loader paints the baked twin with. */
export const LUMINAIRE_TONE = {
  /** COLOR_BODY — matte black aluminium. */
  body: [0.055, 0.058, 0.064] as Vec3,
  /** COLOR_BRASS — brushed brass (the `accent` default). */
  brass: [0.64, 0.46, 0.21] as Vec3,
  /** COLOR_OPAL — the diffuser the light comes out of. */
  opal: [0.92, 0.915, 0.88] as Vec3,
  /** OPAL_TRANSPARENCY — the diffuser is the ONLY transparent part. */
  opalTransparency: 0.22,
} as const;

/** The emitted light colour the house data carries when a light reports none
 *  (luminaire.py `schema.json` → state.colour, warm white). */
export const LUMINAIRE_EMITTED_DEFAULT: Vec3 = [1.0, 0.85, 0.62];

// ─── the accent tones (schema.json `prefs.accent` → luminaire.py ACCENTS) ────

/** The accent choices a luminaire's record carries — the rings, caps, liners. */
export const LUMINAIRE_ACCENTS = ['brass', 'alu', 'black'] as const;
export type LuminaireAccent = (typeof LUMINAIRE_ACCENTS)[number];

/** The module's `ACCENTS` table, verbatim (`luminaire.py`). */
export const LUMINAIRE_ACCENT_RGB: Record<LuminaireAccent, Vec3> = {
  brass: [0.64, 0.46, 0.21],
  alu: [0.56, 0.57, 0.585],
  black: [0.055, 0.058, 0.064],
};

/** True when a stored value names one of the family's accents. */
export function isLuminaireAccent(value: unknown): value is LuminaireAccent {
  return typeof value === 'string' && (LUMINAIRE_ACCENTS as readonly string[]).includes(value);
}

/** True when a stored value names one of the family's variants. */
export function isLuminaireVariant(value: unknown): value is LuminaireVariant {
  return typeof value === 'string' && (LUMINAIRE_VARIANTS as readonly string[]).includes(value);
}

/**
 * The luminaire variant a device name classifies to — `variant_for()` in
 * luminaire.py, mirrored (names are the contract between the hub's devices and
 * the house's own classification; the app only needs it for diagnostics and for
 * the halo sizing fallback when a twin carries no measurable emitter box).
 */
export function luminaireVariantFor(deviceName: string): LuminaireVariant {
  const n = (deviceName || '').toLowerCase();
  if (n.includes('pendant')) return 'pendant';
  if (n.includes('spot')) return 'spot';
  if (n.includes('strip')) return 'strip';
  if (n.includes('bulb')) return 'bulb';
  if (n.includes('counter') || n.includes('panel')) return 'panel';
  if (n.includes('wall') || n.includes('mirror')) return 'wall';
  if (n.includes('pole') || n.includes('outdoor') || n.includes('facade')) return 'pole';
  return 'spot';
}

/** The twin's IFC name for a device: what the pipeline names the fixture. */
export function luminaireTwinName(deviceName: string): string {
  return `Luminaire - ${deviceName}`;
}

/** The prefix every luminaire twin carries (the pipeline's own convention). */
export const LUMINAIRE_TWIN_PREFIX = 'Luminaire - ';

/** The loader's view of one element, as far as the twin lookup needs it. */
export interface BimElementLike {
  expressID: number;
  typeName: string;
  name: string;
}

/** The loader's own type spelling (see bimShadeSpec.findBakedTwinElement). */
function isLightFixture(el: BimElementLike): boolean {
  const t = el.typeName.toUpperCase().replace(/^IFC/, '');
  return t === 'LIGHTFIXTURE';
}

/**
 * The served model's baked luminaire for a device — BY NAME FIRST, deliberately:
 * the hub rebuilds and re-serves the model roughly every two minutes and the
 * appended twins' express ids drift between rebuilds, while the twin's own IFC
 * name (`Luminaire - Bathroom Light`) is what the pipeline derives it from and
 * does not change.
 *
 * `names` carries the candidate spellings (the device's own name first); the
 * pipeline's copy of a device name can differ from the hub's (measured:
 * the hub's `Hue Outdoor Garden` is `Luminaire - Hue Garden` in the model), so
 * the caller may pass more than one. A plain device-name match is NOT one of the
 * candidates on purpose: the model also carries an older, marker-shaped
 * `IfcLightFixture` named exactly like the device, and that element has no
 * diffuser to light.
 */
export function findLuminaireTwinElement(
  elements: readonly BimElementLike[],
  names: readonly string[],
  taken: ReadonlySet<number> = new Set(),
): number | null {
  for (const name of names) {
    const wanted = luminaireTwinName(name);
    const hit = elements.find(
      (el) => el.name === wanted && isLightFixture(el) && !taken.has(el.expressID),
    );
    if (hit) return hit.expressID;
  }
  return null;
}

/** Every luminaire twin in the loaded model (the layer's census). */
export function luminaireTwinIds(elements: readonly BimElementLike[]): number[] {
  return elements
    .filter((el) => isLightFixture(el) && el.name.startsWith(LUMINAIRE_TWIN_PREFIX))
    .map((el) => el.expressID);
}

/** A device's name candidates for the twin lookup, most specific first. */
export function twinNameCandidates(deviceName: string, shortName?: string | null): string[] {
  const out: string[] = [];
  const push = (value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  };
  push(deviceName);
  push(shortName ?? null);
  return out;
}

/** How far a positional match may sit from the widget's own anchor (metres). */
export const TWIN_POSITION_TOLERANCE_M = 1.0;

/**
 * The unclaimed twin nearest a widget's anchor point — the SECOND chance for a
 * device whose name spelling the model does not carry. Positions come from the
 * fixture's own meshes (world metres), so this needs no extra data.
 */
export function nearestLuminaireTwin(
  candidates: ReadonlyArray<{ expressID: number; center: Vec3 }>,
  point: Vec3,
  taken: ReadonlySet<number> = new Set(),
  tolerance: number = TWIN_POSITION_TOLERANCE_M,
): number | null {
  let best: { id: number; distance: number } | null = null;
  for (const candidate of candidates) {
    if (taken.has(candidate.expressID)) continue;
    const distance = Math.hypot(
      candidate.center[0] - point[0],
      candidate.center[1] - point[1],
      candidate.center[2] - point[2],
    );
    if (distance > tolerance) continue;
    if (!best || distance < best.distance) best = { id: candidate.expressID, distance };
  }
  return best?.id ?? null;
}

// ─── the emitter part of a baked twin ──────────────────────────────────────

/** As much of a loaded mesh as the emitter rule reads. */
export interface EmitterMeshLike {
  visible: boolean;
  material: {
    opacity?: number;
    transparent?: boolean;
    color?: { r: number; g: number; b: number };
  };
}

/**
 * Which sub-mesh of a luminaire twin is the EMITTER: the diffuser, i.e. the one
 * part whose style carries the opal tone and the 0.22 transparency. The loader
 * merges geometry per (element, material colour), so a twin arrives as a
 * handful of meshes — the body, the brass accent and the opal lens — and the
 * lens is the transparent one.
 *
 * Two rules, in order: an actually transparent part (the opal style is the only
 * one with transparency in the whole family); then, for a model whose diffuser
 * lost its transparency, the part whose colour sits nearest the opal tone. When
 * neither matches, `null` — the twin has no emitter and the layer skips it
 * rather than lighting the fixture's shell.
 */
export function findEmitterMeshIndex(meshes: readonly EmitterMeshLike[]): number | null {
  if (meshes.length === 0) return null;
  const opacityOf = (mesh: EmitterMeshLike): number =>
    typeof mesh.material.opacity === 'number' ? mesh.material.opacity : 1;
  const transparentIndex = meshes.findIndex(
    (mesh) => mesh.material.transparent === true || opacityOf(mesh) < 0.999,
  );
  if (transparentIndex >= 0) return transparentIndex;
  // Colour-distance fallback: nearest to the opal tone, but only when it is
  // clearly nearer than every other part (a black body and a brass ring must not
  // be mistaken for a diffuser just because one of them is marginally closer).
  const opal = LUMINAIRE_TONE.opal;
  const scored = meshes
    .map((mesh, index) => {
      const c = mesh.material.color;
      if (!c) return { index, distance: Number.POSITIVE_INFINITY };
      const distance = Math.hypot(c.r - opal[0], c.g - opal[1], c.b - opal[2]);
      return { index, distance };
    })
    .sort((a, b) => a.distance - b.distance);
  if (scored.length < 2) return scored[0]?.distance <= 0.35 ? scored[0].index : null;
  if (scored[0].distance <= 0.35 && scored[1].distance - scored[0].distance >= 0.15) {
    return scored[0].index;
  }
  return null;
}

// ─── the live state a light drives ──────────────────────────────────────────

/** A device value row as the light matchers read it. */
interface DeviceValueLike {
  valueKey: string;
  name?: string | null;
  currentValue?: unknown;
}

function getDeviceValues(device: Device): DeviceValueLike[] {
  if (Array.isArray(device.deviceValues)) return device.deviceValues as DeviceValueLike[];
  // Older hub payloads carried the same rows under the legacy `values` alias.
  const legacy = 'values' in device ? (device as { values?: unknown }).values : undefined;
  return Array.isArray(legacy) ? (legacy as DeviceValueLike[]) : [];
}

function normaliseKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

function valueRow(device: Device, keys: readonly string[]): DeviceValueLike | undefined {
  const wanted = new Set(keys.map(normaliseKey));
  return getDeviceValues(device).find((row) => {
    const key = normaliseKey(row.valueKey);
    if (wanted.has(key)) return true;
    return wanted.has(normaliseKey(row.name ?? ''));
  });
}

const POWER_KEYS = ['power', 'on_off', 'state'] as const;
const BRIGHTNESS_KEYS = ['brightness', 'dimming', 'level'] as const;
const COLOUR_KEYS = ['color', 'colour', 'rgb'] as const;

function truthy(value: unknown): boolean {
  return value === true || value === 1 || value === 'on' || value === 'true' || value === '1';
}

function numberOrNull(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * A hub colour value → an sRGB-ish 0..1 triple. The hub reports a hex string
 * (`#ff6b9d`) for the LED integrations, a 0..255 triple for some and a 0..1
 * triple for others; anything else is no colour at all.
 */
export function parseColorValue(value: unknown): Vec3 | null {
  if (typeof value === 'string') {
    const text = value.trim();
    const hex = text.startsWith('#') ? text.slice(1) : text;
    if (/^[0-9a-f]{6}$/i.test(hex)) {
      return [
        parseInt(hex.slice(0, 2), 16) / 255,
        parseInt(hex.slice(2, 4), 16) / 255,
        parseInt(hex.slice(4, 6), 16) / 255,
      ];
    }
    return null;
  }
  if (Array.isArray(value) && value.length >= 3) {
    const parts = value.slice(0, 3).map((n) => numberOrNull(n));
    if (parts.some((n) => n === null)) return null;
    const [r, g, b] = parts as number[];
    const scale = r > 1 || g > 1 || b > 1 ? 255 : 1;
    return [r / scale, g / scale, b / scale];
  }
  return null;
}

/** What the layer needs to light one luminaire right now. */
export interface LightGlowState {
  /** The power flag the hub reports. */
  on: boolean;
  /** 0..100 — 100 for a light that cannot dim. */
  brightness: number;
  /** The emitted colour the light reports, when it reports one. */
  colour: Vec3 | null;
}

/**
 * A device's live glow state, or null when the device reports nothing the layer
 * can drive — an absent light, a device with no power/brightness channel, a
 * widget whose id names something else. `null` is the fallback rule: the baked
 * twin stays exactly as the hub served it.
 */
export function lightGlowState(device: Device | null | undefined): LightGlowState | null {
  if (!device) return null;
  const power = valueRow(device, POWER_KEYS);
  const brightnessRow = valueRow(device, BRIGHTNESS_KEYS);
  if (!power && !brightnessRow) return null;
  const brightness = numberOrNull(brightnessRow?.currentValue);
  const clamped = brightness === null ? 100 : Math.min(100, Math.max(0, brightness));
  const on = power ? truthy(power.currentValue) : clamped > 0;
  const colourRow = valueRow(device, COLOUR_KEYS);
  return {
    on,
    brightness: clamped,
    colour: colourRow ? parseColorValue(colourRow.currentValue) : null,
  };
}

/**
 * The emitter's emissive strength: `on` → the brightness fraction, off → 0.
 * A light switched on at 0 % emits nothing and is drawn as off — the hub's own
 * meaning of the pair (measured on the demo: `Hue Desk Office` sits at
 * power=true, brightness=0 and its lens reads dark).
 */
export function glowIntensity(state: LightGlowState | null | undefined): number {
  if (!state || !state.on) return 0;
  return Math.min(1, Math.max(0, state.brightness / 100));
}

/** The colour the light emits — its own when reported, the house default else. */
export function glowColour(state: LightGlowState | null | undefined): Vec3 {
  return state?.colour ?? LUMINAIRE_EMITTED_DEFAULT;
}

/**
 * The halo's share of the glow (the sprite's opacity), by brightness.
 *
 * PROPORTIONAL, with no floor. It used to be `0.12 + 0.48 × intensity`, so at 6 %
 * brightness the sprite still drew at 0.15 alpha — a lit-looking blob on a lamp that was
 * turned almost off. That is why dimming appeared to do nothing (owner, 2026-10-21:
 * *"i see no light intensity changes when i change the dimm level of the lights"*): the
 * emissive followed the dim and the halo did not.
 */
export function haloOpacity(state: LightGlowState | null | undefined): number {
  const intensity = glowIntensity(state);
  if (intensity <= 0) return 0;
  return Math.min(0.6, 0.55 * intensity);
}

/** Halo diameter (metres) for an emitter whose largest box side is `maxDimM`.
 *  Floored so a small lens still reads as a glow, capped so a linear luminaire
 *  cannot paint a room-sized blob (a multi-halo run along a strip profile is a
 *  v2 refinement, not a v1 defect). */
export function haloDiameter(maxDimM: number): number {
  const value = Number.isFinite(maxDimM) && maxDimM > 0 ? maxDimM : 0.12;
  // A tighter cap: 1.6 m of additive sprite is a lamp-shaped blob on a wall, not a glow.
  return Math.min(1.1, Math.max(0.3, value * 2.2));
}
